/*
 * DISPLAY FORMATTERS — the ONE place a raw number becomes a string a person reads.
 *
 * There were two cost formatters, one in each app, and they disagreed: the same $0.0412 rendered as
 * "$0.04" on the transcript list and "$0.041" on the usage dashboard. Nobody would call that a bug
 * in isolation, and together they are the thing that makes a reader distrust both numbers — a
 * console whose figures do not match itself is a console you have to go and verify elsewhere.
 *
 * Here rather than in either app, because the rule is a product decision (how much precision does a
 * price deserve) and not a page's business.
 *
 * DATES AND NUMBERS FOLLOW THE APP'S LANGUAGE, NOT THE BROWSER'S. `toLocaleString()` with no
 * argument asks the operating system, so a console switched to French rendered '8/18/2026, 11:14:53
 * PM' — American order, American clock — on a page whose every other word was French. The switcher
 * did nothing to the one part of a row a reader uses to line an event up against a controller's own
 * log. Every formatter here reads `getLocale()`.
 */
import { getLocale, formatNumber } from './i18n';
/**
 * Money, at the precision the number actually has.
 *
 * PRECISION FOLLOWS MAGNITUDE, because a column showing "$0.0002341" beside "$1,204.00" is
 * unreadable, and rounding everything to cents erased this product's economics from the panel
 * entirely — a voice window is about $0.002 and a text turn less, so a whole-dollar view reported
 * "$0" against real spend and made a working meter look broken.
 *
 *   ≥ $1k → "$1.2k"   ≥ $1 → "$4.20"   ≥ 1¢ → "$0.043"   below → "$0.0021"   exactly 0 → "$0"
 */
export function fmtCost(n) {
    if (n === 0)
        return '$0';
    if (n >= 1000)
        return `$${(n / 1000).toFixed(1)}k`;
    if (n >= 1)
        return `$${n.toFixed(2)}`;
    if (n >= 0.01)
        return `$${n.toFixed(3)}`;
    // Two significant figures, so a very small number still says something rather than collapsing to
    // "$0.00" — which reads as free, and free is the one thing it is not.
    return `$${n.toFixed(Math.min(8, Math.max(4, 1 - Math.floor(Math.log10(n)))))}`;
}
/**
 * A count, grouped — or an em dash when it was never recorded.
 *
 * ABSENT IS NOT ZERO. A conversation older than token attribution has nothing recorded, and a
 * confident "0" would say it used no tokens rather than that nobody counted.
 */
export function fmtNum(n) {
    return typeof n === 'number' ? formatNumber(n) : '—';
}
/**
 * Date/time formatters, memoised per (locale, options) — the same shape as `formatNumber`, for the
 * same reason: constructing an `Intl.DateTimeFormat` is expensive relative to formatting with one,
 * and these run per row of a grid that holds hundreds.
 *
 * A locale switch does not have to invalidate anything: the cache is keyed BY locale, so the old
 * entries simply stop being asked for.
 */
const dtCache = new Map();
function dtf(opts) {
    const locale = getLocale();
    const key = `${locale}:${JSON.stringify(opts)}`;
    let f = dtCache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, opts);
        dtCache.set(key, f);
    }
    return f;
}
/**
 * The date alone. Numeric, with a FOUR-DIGIT year — `dateStyle: 'short'` abbreviates it to '8/18/26',
 * and a two-digit year on an audit surface is a reading somebody has to make twice.
 */
export function fmtDate(at, opts = { year: 'numeric', month: 'numeric', day: 'numeric' }) {
    return dtf(opts).format(at);
}
/*
 * WHICH FIELDS INTL TREATS AS "WHAT TO SHOW". Everything else — hour12, hourCycle, timeZone,
 * numberingSystem — is a MODIFIER: it changes how a field is rendered and does not request one.
 */
const SHOWS_SOMETHING = [
    'dateStyle',
    'timeStyle',
    'weekday',
    'era',
    'year',
    'month',
    'day',
    'dayPeriod',
    'hour',
    'minute',
    'second',
    'fractionalSecondDigits',
];
/**
 * The time alone, to the second: an audit surface lines events up against a controller's own log.
 *
 * MODIFIER-ONLY OPTIONS KEEP THE TIME. Intl's rule is that when a request names no field at all it
 * falls back to year+month+day — so `fmtTime(d, { hour12: false })`, which reads exactly like "the
 * time, 24-hour", returns a DATE. The controller Logs tab rendered "Sep 2 9/2/2026" for months
 * because of it: a date, then a function called fmtTime returning a second date.
 *
 * A caller that names a field still gets precisely what it asked for, including the styles, which
 * Intl refuses to mix with components. This only fills in the fields for a caller that named none —
 * where the alternative is a function called fmtTime that does not return a time.
 */
export function fmtTime(at, opts = { timeStyle: 'medium' }) {
    const asked = SHOWS_SOMETHING.some((k) => opts[k] !== undefined);
    return dtf(asked ? opts : { timeStyle: 'medium', ...opts }).format(at);
}
/** Both, for the places that show one instant in one cell. */
export function fmtDateTime(at, 
// Spelled out rather than `dateStyle`/`timeStyle`: Intl REJECTS mixing a style with individual
// components, and mixing them is the natural thing to write when you want a four-digit year and a
// seconds-precision clock. It throws at the call site, not here.
opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
}) {
    return dtf(opts).format(at);
}
/**
 * Bytes, in the unit we BILL in.
 *
 * Decimal (10³) rather than binary (2¹⁰), deliberately and everywhere: Cloudflare bills us in GB
 * and every competitor a customer compares us against quotes GB, so a page that renders 2³⁰ under
 * the label "GB" shows a number 7.4% smaller than the one on the invoice. Two units in one product
 * is a support ticket that nobody can close.
 */
export function fmtBytes(n) {
    if (!Number.isFinite(n) || n <= 0)
        return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log10(n) / 3));
    const v = n / 1000 ** i;
    // One decimal below 10 so "1.4 GB" does not round to "1 GB", none above so "428 MB" stays legible.
    return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
/** A coarse elapsed duration (Intl-localized): "45 sec", "3 hr", "12 days",
 *  "3 wk", "4 mo", "2 yr". One unit, whole number — it labels how long a
 *  device has been recording, where "3 wk" is the whole answer. '' for
 *  non-positive input. Locale from the document (i18n keeps it there). */
export function fmtDuration(ms, locale = document.documentElement.lang || 'en') {
    if (!(ms > 0))
        return '';
    const sec = ms / 1000;
    const ladder = [
        [1, 'second'],
        [60, 'minute'],
        [3600, 'hour'],
        [86_400, 'day'],
        [7 * 86_400, 'week'],
        [30 * 86_400, 'month'],
        [365 * 86_400, 'year'],
    ];
    let i = 0;
    while (i + 1 < ladder.length && sec >= ladder[i + 1][0])
        i++;
    const v = Math.max(1, Math.floor(sec / ladder[i][0]));
    return new Intl.NumberFormat(locale, { style: 'unit', unit: ladder[i][1], unitDisplay: 'short' }).format(v);
}
/** Relative "how long ago" (Intl-localized): "3 min. ago", "2 hr. ago". '' when
 *  the timestamp is unset. Locale from the document (i18n keeps it there). */
export function fmtAgo(atMs, locale = document.documentElement.lang || 'en') {
    if (!(atMs > 0))
        return '';
    const sec = Math.max(0, Math.round((Date.now() - atMs) / 1000));
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'short' });
    if (sec < 60)
        return rtf.format(-sec, 'second');
    if (sec < 3600)
        return rtf.format(-Math.round(sec / 60), 'minute');
    if (sec < 86_400)
        return rtf.format(-Math.round(sec / 3600), 'hour');
    return rtf.format(-Math.round(sec / 86_400), 'day');
}
//# sourceMappingURL=format.js.map